// O QUE CADA EVENTO DA TRILHA SIGNIFICA — rótulo curto e explicação de hover.
//
// ## 🔴 POR QUE ESTE ARQUIVO NÃO COBRE TODOS OS 101 EVENTOS
//
// Descrição de evento numa tela de AUDITORIA é documentação, e documentação inventada é pior
// que ausente: quem audita toma decisão a partir dela. A maior parte do catálogo
// (`packages/core/src/audit/events.ts`) não tem comentário explicando o ato, e escrever uma
// frase plausível para cada um seria fabricar significado — o "fallback plausível esconde
// ausência" do CLAUDE.md aplicado ao pior lugar possível.
//
// Então aqui só entra o que dá para FUNDAMENTAR: no comentário do catálogo, na rota que emite,
// ou no ADR citado. O que não está descrito cai no slug cru — que é exatamente o que a tela
// já mostrava, e é honesto.
//
// ⚠️ A LISTA DO FILTRO é outra coisa e NÃO mora aqui: ela é derivada do catálogo em
// `AuditPage.tsx`, mecanicamente, justamente para não envelhecer. Descrição é editorial;
// enumeração é mecânica. Misturar as duas foi o defeito de 24/08 (o filtro tinha 16 de 101).

/** Rótulo curto — o que a coluna "Evento" mostra. */
export const ROTULOS: Readonly<Record<string, string>> = {
  // --- Acesso à denúncia por membro que entrou depois do submit (23–24/08) ---
  acesso_solicitado: 'Pediu acesso à denúncia',
  acesso_autorizado: 'Autorizou o acesso (1 das 2 cadeiras)',
  acesso_negado: 'Negou o acesso',
  acesso_concedido: 'Chave de leitura entregue',
  acesso_concedido_via_escrow: '⚠ Chave entregue pela custódia (escrow)',
  membros_rewrap_v2: 'Chave redistribuída a membros',
  comite_keypair_enrolled: 'Membro cadastrou a chave de leitura',
  // --- Cofre / break-glass (ADR-0015) ---
  cofre_acesso_solicitado: 'Cofre: pedido de break-glass aberto',
  cofre_assinatura_registrada: 'Cofre: assinatura do quórum',
  cofre_assinatura_rejeitada: 'Cofre: assinatura rejeitada',
  cofre_acesso_concedido: '⚠ Cofre: teor recuperado (break-glass)',
  cofre_acesso_revogado_24h: 'Cofre: janela revogada',
  cofre_share_distribuido: 'Cofre: share distribuído',
  // --- Denúncia, lado do denunciante ---
  denuncia_aberta: 'Denúncia enviada',
  status_consultado: 'Denunciante consultou o andamento',
  info_complementar_adicionada: 'Denunciante enviou complemento',
  mensagem_denunciante_recebida: 'Denunciante respondeu ao comitê',
  attachment_uploaded: 'Anexo enviado',
  // --- Rito ---
  triagem_classificada: 'Triagem classificada',
  triagem_bloqueada_composicao: 'Triagem bloqueada por composição incompleta',
  triagem_relato_retaliacao: 'Triagem marcou como retaliação',
  triagem_conflito_concluida: 'Triagem de conflito concluída pela IA',
  triagem_conflito_liberada_sem_analise: '⚠ Liberada SEM análise de conflito',
  conflito_declarado: 'Membro declarou (não) ter conflito',
  denuncia_arquivada_triagem: 'Arquivada na triagem',
  denuncia_encaminhada_triagem: 'Encaminhada na triagem',
  instrucao_evidencia_anexada: 'Evidência anexada à instrução',
  plano_investigacao_gerado: 'Plano de investigação gerado',
  investigacao_prorrogada: 'Investigação prorrogada',
  oitiva_registrada: 'Oitiva registrada',
  oitiva_dispensada: 'Oitiva dispensada',
  oitiva_ata_anexada: 'Ata da oitiva anexada',
  oitiva_fechamento_votado: 'Voto de fechamento da oitiva',
  oitivas_encerradas: 'Fase de oitivas encerrada',
  parecer_emitido: 'Parecer emitido',
  quorum_fechado_v2: 'Quórum fechado',
  decisao_emitida: 'Decisão emitida',
  retorno_definido_v2: 'Retorno ao denunciante definido',
  prazo_recursal_aberto: 'Prazo de recurso aberto',
  caso_encerrado: 'Caso encerrado',
  notificacao_denunciante_enviada: 'Denunciante notificado',
  notificacao_denunciado_enviada: 'Denunciado notificado',
  // --- Recurso ---
  recurso_interposto: 'Recurso interposto',
  recurso_parecer_emitido: 'Parecer do recurso emitido',
  recurso_decidido: 'Recurso decidido',
  notificacao_recurso_enviada: 'Notificação de recurso enviada',
  // --- Composição do comitê ---
  membro_adicionado: 'Membro adicionado ao comitê',
  membro_removido: 'Membro removido do comitê',
  membro_impedido_v2: 'Membro declarou impedimento',
  substituto_designado_v2: 'Substituto designado',
  composicao_alterada: 'Composição do comitê alterada',
  membro_externo_obrigatorio_solicitado: 'Membro externo exigido para o caso',
  membro_externo_indicado: 'Membro externo indicado',
  membro_externo_voto: 'Voto na indicação do membro externo',
  membro_externo_retirado: 'Indicação de membro externo retirada',
  caso_assumido_unipessoal: '⚠ Caso assumido em gestão unipessoal',
  conducao_unipessoal_encerrada: 'Condução unipessoal encerrada',
  conducao_delegada: 'Condução delegada a outros membros',
  // --- Contas e autenticação ---
  usuario_criado: 'Conta criada',
  usuario_desativado: 'Conta desativada',
  usuario_ativado: 'Conta reativada',
  admin_master_criado: '⚠ Gestor unico designado',
  senha_alterada: 'Senha alterada',
  policy_accepted: 'Aceitou a política de não-retaliação (LGP-02)',
  login_success: 'Login bem-sucedido',
  login_failed: 'Tentativa de login falhou',
  account_locked: '⚠ Conta bloqueada por tentativas',
  session_revoked: 'Sessão revogada',
  // --- IA de apoio ---
  ia_analista_parecer: 'IA-Analista emitiu parecer de apoio',
  ia_auditora_validacao: 'IA-Auditora validou o rito',
  ia_auditora_block: '⚠ IA-Auditora vetou a transição',
  // --- Hardening / LGPD ---
  legal_export_executado: '⚠ Export judicial da trilha executado',
  dado_purgado: '⚠ Dados purgados (retenção/LGPD)',
  legal_hold_alterado: 'Legal hold alterado',
};

/**
 * A explicação de HOVER — o que aquele ato significa, em uma frase.
 *
 * Cada entrada é fundamentada no comentário do catálogo, na rota que a emite, ou no ADR
 * citado. Nenhuma foi inferida do nome.
 */
export const DESCRICOES: Readonly<Record<string, string>> = {
  acesso_solicitado:
    'Um membro do comitê cuja conta foi criada DEPOIS desta denúncia pediu autorização para ler o teor. Ele não recebeu chave nenhuma com este ato — só registrou o pedido.',
  acesso_autorizado:
    'Um administrador autorizou o pedido. São necessárias DUAS autorizações, de contas diferentes: uma de Admin de TI e uma de Admin Master. Autorizar concede o DIREITO de ler, não a chave.',
  acesso_negado:
    'Um administrador recusou o pedido. A rodada é encerrada e as autorizações já dadas são zeradas; pedir de novo abre uma rodada nova.',
  acesso_concedido:
    'A chave de leitura foi efetivamente entregue ao solicitante — a partir daqui ele lê o teor. O embrulho da chave é gerado no navegador de quem já lê o caso (o servidor é cego, ADR-0002).',
  acesso_concedido_via_escrow:
    'A chave foi recuperada da CUSTÓDIA (escrow) pelo servidor, sem passar pelo quórum 2-de-3 do cofre. Existe para o caso encerrado em que o comitê foi todo substituído e ninguém mais tem a chave. Exige TOTP e as duas autorizações.',
  membros_rewrap_v2:
    'Um membro que já lê o caso re-embrulhou a chave de leitura para outros membros. É o caminho normal de distribuição de chave, feito no navegador dele.',
  comite_keypair_enrolled:
    'O membro gerou seu par de chaves no navegador e cadastrou a metade pública. Sem isso ele não pode receber a chave de nenhuma denúncia.',
  cofre_acesso_solicitado:
    'Alguém abriu um pedido de break-glass — acesso de emergência ao teor, fora do rito do comitê. O motivo é guardado cifrado; a trilha registra só o hash dele.',
  cofre_assinatura_registrada:
    'Um responsável pela custódia assinou o pedido de break-glass. São necessárias 2 assinaturas de 3 (ADR-0015), cada uma com TOTP próprio.',
  cofre_assinatura_rejeitada:
    'Tentativa de assinar um pedido que já estava concedido ou revogado. Registrado para evidência forense da corrida entre o cron e a assinatura.',
  cofre_acesso_concedido:
    'O break-glass foi consumado: o teor da denúncia foi recuperado EM CLARO e entregue a quem abriu o pedido. É o ato mais sensível do sistema.',
  cofre_acesso_revogado_24h:
    'A janela do pedido de break-glass foi encerrada — por revogação manual ou pelo cron que expira em 24h.',
  cofre_share_distribuido: 'Distribuição de share do cofre (maquinaria anterior ao ADR-0015).',
  denuncia_aberta:
    'Uma denúncia foi recebida pelo canal. O servidor nunca viu o teor em claro: ele chega já cifrado do navegador do denunciante.',
  status_consultado:
    'O denunciante consultou o andamento com protocolo e frase-passe. Ato anônimo — a trilha não registra quem, porque não há quem registrar.',
  info_complementar_adicionada:
    'O denunciante acrescentou informação à denúncia depois do envio, também cifrada.',
  mensagem_denunciante_recebida:
    'O denunciante respondeu ao comitê pelo canal de mensagens, pela rota pública atrás do firewall de anonimato.',
  attachment_uploaded: 'Um anexo foi enviado e guardado cifrado.',
  triagem_classificada:
    'O comitê classificou a gravidade e a rota do caso, abrindo o rito de apuração.',
  triagem_bloqueada_composicao:
    'A triagem foi impedida porque a composição mínima do comitê (RH + Jurídico + SESMT, Lei 14.457 art. 23) não estava completa.',
  triagem_relato_retaliacao:
    'A triagem marcou o relato como retaliação — indicador exigido pela política de não-retaliação (LGP-02).',
  triagem_conflito_concluida:
    'A IA leu o teor ANTES de qualquer humano do comitê e disse se o denunciado é membro do comitê. Enquanto isso não conclui, o caso fica invisível para o comitê inteiro.',
  triagem_conflito_liberada_sem_analise:
    'A IA não respondeu depois das tentativas previstas e o caso foi liberado ao comitê SEM análise de conflito. O denunciado pode estar entre quem vê o caso.',
  conflito_declarado:
    'Um membro declarou formalmente se tem ou não conflito de interesse no caso. O teor só é liberado depois que todos os exigíveis declararem.',
  denuncia_arquivada_triagem:
    'O caso foi arquivado já na triagem, com motivo de uma lista fechada. A justificativa em texto fica cifrada; a trilha guarda só o motivo.',
  denuncia_encaminhada_triagem:
    'O caso foi encaminhado a outra instância na triagem, com destino de uma lista fechada.',
  instrucao_evidencia_anexada: 'Uma evidência foi anexada à fase de instrução do caso.',
  plano_investigacao_gerado: 'O plano de investigação do caso foi gerado e compartilhado.',
  investigacao_prorrogada:
    'O prazo da investigação foi prorrogado — prorrogação prevista pelo rito, com limite.',
  oitiva_registrada:
    'Uma oitiva foi registrada. Sem oitiva não há decisão: a ampla defesa é bloqueio estrutural do rito, não aviso.',
  oitiva_dispensada: 'Uma oitiva foi formalmente dispensada, com justificativa registrada.',
  oitiva_ata_anexada:
    'A ata (ou justificativa) da oitiva foi anexada cifrada, fechando a pendência de anexo.',
  oitiva_fechamento_votado: 'Um membro votou pelo fechamento da fase de oitivas.',
  oitivas_encerradas:
    'A fase de oitivas foi explicitamente encerrada, movendo o caso para avaliação.',
  parecer_emitido: 'Um membro do comitê emitiu seu parecer sobre o caso.',
  quorum_fechado_v2: 'O quórum do colegiado foi atingido para a etapa.',
  decisao_emitida:
    'O comitê emitiu a decisão final, sempre humana e colegiada — a IA é apoio e não decide (ADR-0014).',
  retorno_definido_v2: 'O retorno a ser dado ao denunciante foi definido.',
  prazo_recursal_aberto:
    'O comitê abriu o prazo de recurso. Recurso não é direito automático do denunciante: quem abre o prazo é o comitê.',
  caso_encerrado: 'O caso foi encerrado.',
  notificacao_denunciante_enviada: 'O denunciante foi notificado de um andamento.',
  notificacao_denunciado_enviada: 'O denunciado foi notificado.',
  recurso_interposto: 'Um recurso foi interposto dentro do prazo aberto pelo comitê.',
  recurso_parecer_emitido: 'Um parecer foi emitido na instância de recurso.',
  recurso_decidido: 'O recurso foi decidido.',
  notificacao_recurso_enviada: 'Notificação sobre o recurso foi enviada.',
  membro_adicionado: 'Uma conta passou a integrar o comitê.',
  membro_removido: 'Uma conta deixou de integrar o comitê.',
  membro_impedido_v2:
    'Um membro se declarou impedido no caso; ele sai do quórum e o caso pode precisar de substituto.',
  substituto_designado_v2:
    'O Admin de TI designou um substituto de uma lista pré-aprovada. Designar não dá acesso a conteúdo (ADR-0002).',
  composicao_alterada: 'A composição do comitê mudou.',
  membro_externo_obrigatorio_solicitado:
    'O caso passou a exigir um membro externo do Comitê de Recurso.',
  membro_externo_indicado:
    'Um nome foi indicado como membro externo. Indicar zera votos anteriores: aprovar um nome não é aprovar outro.',
  membro_externo_voto:
    'Voto do colegiado na indicação do membro externo. A aprovação é UNÂNIME entre os elegíveis, e uma reprovação derruba a indicação.',
  membro_externo_retirado: 'A indicação de membro externo foi retirada por quem indicou.',
  caso_assumido_unipessoal:
    'O Admin Master assumiu o caso sozinho (ADR-0022). A partir daqui o caso fica invisível para o resto do comitê, e só ele é cobrado nas etapas.',
  conducao_unipessoal_encerrada:
    'A condução unipessoal terminou e o caso voltou ao rito colegiado.',
  conducao_delegada:
    'O condutor passou o caso a membros escolhidos; só eles seguem com ele e só eles são cobrados.',
  usuario_criado: 'Uma conta foi criada, com link de primeiro acesso válido por 30 minutos.',
  usuario_desativado:
    'Uma conta foi desativada. Conta nunca é apagada — o ciclo de vida é auditado (ADR-0021).',
  usuario_ativado: 'Uma conta desativada foi reativada.',
  admin_master_criado:
    'Foi criada a conta do GESTOR UNICO (admin_master). Ela le o teor dos casos que enxerga, ' +
    'pode conduzir um caso sozinha, alcanca o cofre de emergencia e ocupa uma das duas ' +
    'cadeiras que autorizam acesso a denuncia. Existe no maximo UMA ativa por vez: trocar a ' +
    'pessoa e desativar a atual e criar a nova.',
  senha_alterada: 'A senha da conta foi alterada.',
  policy_accepted:
    'A pessoa aceitou a política de não-retaliação (LGP-02), na versão vigente. Sem o aceite, as rotas do rito são recusadas.',
  login_success: 'Autenticação bem-sucedida.',
  login_failed: 'Tentativa de autenticação falhou. Cinco falhas em 15 minutos bloqueiam a conta.',
  account_locked: 'A conta foi bloqueada por excesso de tentativas de login.',
  session_revoked: 'Uma sessão foi revogada.',
  ia_analista_parecer:
    'A IA-Analista emitiu parecer técnico de APOIO. Ela não vota o mérito e não bloqueia o avanço do rito (ADR-0014).',
  ia_auditora_validacao:
    'A IA-Auditora verificou a conformidade do rito e devolveu um veredito (ok / pendência / veto).',
  ia_auditora_block:
    'A IA-Auditora VETOU uma transição do rito por falta de devido processo. O gate é fail-closed: sem resposta dela, a etapa não avança.',
  legal_export_executado:
    'A trilha de auditoria foi exportada para uso judicial, assinada. Cada invocação é registrada ANTES de a exportação retornar.',
  dado_purgado:
    'Dados de um caso foram purgados por retenção ou pedido do titular (LGPD). A chave é destruída: o teor deixa de ser recuperável, inclusive pelo cofre.',
  legal_hold_alterado:
    'O bloqueio de retenção (legal hold) de um caso foi ligado ou desligado pelo Admin de TI.',
};

/** O rótulo curto; sem rótulo próprio, humaniza o slug (underscore → espaço). */
export function rotuloEvento(ev: string): string {
  if (ev === '') return '';
  const proprio = ROTULOS[ev];
  if (proprio !== undefined) return proprio;
  const s = ev.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * O texto de hover.
 *
 * Com descrição: a frase + o slug entre parênteses, porque o auditor precisa do valor cru
 * para cruzar com o export judicial. Sem descrição: só o slug — nunca uma frase inventada.
 */
export function descricaoEvento(ev: string): string {
  if (ev === '') return '';
  const d = DESCRICOES[ev];
  return d === undefined ? ev : `${d}\n\n(${ev})`;
}
